import type { OrderStateEnum } from '@cineamo/api/models/order/OrderStateEnum.types';

/**
 * A failure a repeat may cure: no answer at all (network error, timeout abort), 408, 429 or a 5xx. Any other
 * 4xx is the server's answer about this request, e.g. 404 for a cart that does not exist (CIN-15436).
 */
export function isTransientRequestError(error: unknown): boolean {
    const status = (error as { status?: unknown } | null)?.status;
    if (typeof status !== 'number' || status === 0) return true;
    return status === 408 || status === 429 || status >= 500;
}

/**
 * What a failed cart read means for the cart the guest holds: no answer keeps it (its seat locks are the guest's) and
 * lets them retry, "paid" is the API saying so (reason_code of ReasonCode.CART_IS_PAID), any other answer means the
 * cart is gone or not theirs, and a fresh one replaces it.
 */
export function cartLoadOutcome(error: unknown): 'keep-and-retry' | 'paid' | 'replace' {
    if (isTransientRequestError(error)) return 'keep-and-retry';
    return (error as { data?: { reason_code?: unknown } } | null)?.data?.reason_code === 'cart_is_paid'
        ? 'paid'
        : 'replace';
}

/** Runs `run`, repeating it after each of `delaysMs` while it fails transiently; rethrows the last failure. */
export async function retryTransient<T>(
    run: () => Promise<T>,
    {
        delaysMs = [1_000, 2_000, 4_000],
        sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))
    }: { delaysMs?: number[]; sleep?: (ms: number) => Promise<void> } = {}
): Promise<T> {
    for (let attempt = 0; ; attempt++) {
        try {
            return await run();
        } catch (error) {
            if (attempt >= delaysMs.length || !isTransientRequestError(error)) throw error;
            await sleep(delaysMs[attempt]);
        }
    }
}

/**
 * What the page after "Pay" may say about the order. Only a fulfilled order is a booking: the payment is merely
 * authorized when the checkout lands there, capture and the ticket system follow in the webhook.
 */
export type PaidCartVerdict = 'confirmed' | 'pending' | 'unconfirmed' | 'booking-failed' | 'payment-failed';

export function paidCartVerdict(
    // The enum's values, not the enum: a value import would not load under `node --test`.
    orderState: `${OrderStateEnum}` | null | undefined,
    isPollLimitReached: boolean
): PaidCartVerdict {
    if (orderState === 'fulfilled') return 'confirmed';
    if (!orderState || orderState === 'pending') return isPollLimitReached ? 'unconfirmed' : 'pending';
    if (orderState === 'refunded_fulfillment_failed') return 'booking-failed';
    return 'payment-failed';
}
